import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { STORAGE_KEYS } from '../utils/constants'

export type Theme = 'light' | 'dark'

interface ThemeContextValue {
  theme: Theme
  toggle: () => void
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function initialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEYS.theme)
  if (stored === 'light' || stored === 'dark') return stored
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark'
  return 'light'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(initialTheme)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.theme, theme)
    // shadcn-Konvention: `:root` = Light, `.dark` = Dark. Die dark-Klasse am
    // documentElement (<html>) aktiviert die Dark-Tokens aus ui/theme.css; sie
    // kaskadiert in den `.vcard-editor-root`-Wrapper. Single source of truth für
    // die Konsolen-Optik ist ui/theme.css (BMC-Tokens).
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  const setTheme = useCallback((t: Theme) => setThemeState(t), [])
  const toggle = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), [])

  return <ThemeContext.Provider value={{ theme, toggle, setTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
